import React, { useEffect, useRef, useState } from "react";

// One financing position, told the way the money actually moves: a line
// agreed and drawn on, then a share of every receipt on the title redirected
// to the financier until the premium is repaid, and nothing after that.
// Terms follow the production walk of 7 Sep 2026 (120% premium, half of
// receipts, 20% floor) scaled to film-sized illustrative sums.
const PREMIUM = 1.2;
const FLOOR = 20;

const frames = [
  {
    label: "Day one",
    title: "A line of $200,000, and the first draw lands",
    desc: "Financier and rights holder agree the terms: repaid at 120%, from half of everything the title earns. Both sign, and the first $100,000 lands with the rights holder.",
    drawn: 100000,
    repaid: 0,
    flows: ["draw"],
    nodes: ["financier", "rh"],
    dur: 14000,
  },
  {
    label: "Pre-production",
    title: "The line is a ceiling. Draws are at the rights holder’s request.",
    desc: "They ask for $50,000 more and the financier funds it. Repayment is owed on what was drawn, not on the whole line.",
    drawn: 150000,
    repaid: 0,
    flows: ["draw"],
    nodes: ["financier", "rh"],
    dur: 11000,
  },
  {
    label: "Release",
    title: "Every sale repays the financier, automatically",
    desc: "A purchase divides between platform and rights holder exactly as their deal says. Half of the rights holder’s share then goes to the financier, the moment the sale happens.",
    drawn: 150000,
    repaid: 21000,
    flows: ["saleRh", "salePlat", "repay"],
    nodes: ["viewer", "platform", "rh", "financier"],
    dur: 14000,
  },
  {
    label: "A distribution deal",
    title: "An advance counts as a receipt too",
    desc: "A platform pays $50,000 up front for the title and half goes to the financier the same way. No single deal can step ahead of the person who funded the film.",
    drawn: 150000,
    repaid: 46000,
    flows: ["advance", "repay"],
    nodes: ["platform", "rh", "financier"],
    dur: 13000,
  },
  {
    label: "Ongoing sales",
    title: "The platform never sees a difference",
    desc: "Its split is untouched and it signed nothing. While anything is outstanding, every new deal on the title must leave the rights holder at least 20% of each sale.",
    drawn: 150000,
    repaid: 120000,
    flows: ["saleRh", "salePlat", "repay"],
    nodes: ["viewer", "platform", "rh", "financier"],
    dur: 13000,
  },
  {
    label: "Settled",
    title: "Repaid in full. The claim ends.",
    desc: "The last of the $180,000 is repaid and the position closes for good. The rights holder’s share is theirs in full again, and the financier never owned any part of the film.",
    drawn: 150000,
    repaid: 180000,
    flows: ["saleRh", "salePlat"],
    nodes: ["viewer", "platform", "rh"],
    done: true,
    dur: 16000,
  },
];

const NODES = {
  viewer: { x: 134, y: 6, label: "Viewer" },
  financier: { x: 6, y: 88, label: "Financier" },
  platform: { x: 262, y: 88, label: "Platform" },
  rh: { x: 134, y: 170, label: "Rights holder" },
};

const FLOWS = {
  saleRh: { d: "M180 38 L180 166", color: "#e458b9", marker: "pink" },
  salePlat: { d: "M218 38 L274 86", color: "rgba(255,255,255,0.65)", marker: "white" },
  draw: { d: "M86 120 L142 168", color: "#a653f6", marker: "purple" },
  advance: { d: "M274 120 L218 168", color: "#e458b9", marker: "pink" },
  repay: { d: "M130 190 H52 V122", color: "#f59e0b", marker: "amber" },
};

const MARKERS = {
  dim: "rgba(255,255,255,0.25)",
  pink: "#e458b9",
  purple: "#a653f6",
  amber: "#f59e0b",
  white: "rgba(255,255,255,0.75)",
};

const LABELS = [
  { text: "to rights holder", x: 174, y: 72, anchor: "end" },
  { text: "to platform", x: 250, y: 52, anchor: "start" },
  { text: "draws", x: 120, y: 136, anchor: "start" },
  { text: "advance", x: 240, y: 136, anchor: "end" },
  { text: "share of receipts", x: 91, y: 207, anchor: "middle" },
];

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

// Eases a displayed figure towards its target so the ledger counts rather
// than jumps. Snaps when asked (the loop restarting) or under reduced motion.
function useTween(target, snap, reduced) {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);

  useEffect(() => {
    if (snap || reduced || fromRef.current === target) {
      fromRef.current = target;
      setValue(target);
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / 700);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (target - from) * eased);
      fromRef.current = v;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, snap, reduced]);

  return value;
}

export default function FinancingFlowAnimation() {
  const [reduced] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const [index, setIndex] = useState(reduced ? frames.length - 1 : 0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (reduced) return;
    timerRef.current = setTimeout(
      () => setIndex((i) => (i + 1) % frames.length),
      frames[index].dur
    );
    return () => clearTimeout(timerRef.current);
  }, [index, reduced]);

  const frame = frames[index];
  const snap = index === 0;
  const drawn = useTween(frame.drawn, snap, reduced);
  const repaid = useTween(frame.repaid, snap, reduced);
  const owed = Math.round(drawn * PREMIUM);
  const pct = owed > 0 ? Math.min(100, (repaid / owed) * 100) : 0;

  return (
    <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 sm:p-8">
      <div
        key={index}
        className="min-h-[10rem] animate-[fin-fade_0.4s_ease] sm:min-h-[8.5rem]"
      >
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-lightpurple">
          {frame.label}
        </p>
        <p className="mt-2 text-lg font-semibold text-white">{frame.title}</p>
        <p className="mt-1 text-sm/6 text-gray-300">{frame.desc}</p>
      </div>

      <svg
        viewBox="0 0 360 216"
        className="mt-5 w-full"
        aria-hidden="true"
        style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
      >
        <defs>
          {Object.entries(MARKERS).map(([id, color]) => (
            <marker
              key={id}
              id={`fin-m-${id}`}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="9"
              markerHeight="9"
              markerUnits="userSpaceOnUse"
              orient="auto"
            >
              <path
                d="M2 1L8 5L2 9"
                fill="none"
                stroke={color}
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </marker>
          ))}
        </defs>

        {/* Static routes, always faintly visible */}
        {Object.entries(FLOWS).map(([id, f]) => (
          <path
            key={`base-${id}`}
            d={f.d}
            fill="none"
            stroke="rgba(255,255,255,0.14)"
            strokeWidth="1"
            markerEnd="url(#fin-m-dim)"
          />
        ))}

        {/* Money moving on the routes this frame uses */}
        {Object.entries(FLOWS).map(([id, f]) => (
          <path
            key={`flow-${id}`}
            d={f.d}
            fill="none"
            stroke={f.color}
            strokeWidth="2"
            strokeLinecap="round"
            markerEnd={`url(#fin-m-${f.marker})`}
            className={`fin-fl ${frame.flows.includes(id) ? "on" : ""}`}
          />
        ))}

        {LABELS.map((l) => (
          <text
            key={l.text}
            x={l.x}
            y={l.y}
            fontSize="10"
            textAnchor={l.anchor}
            fill="#8b8fa4"
          >
            {l.text}
          </text>
        ))}

        {Object.entries(NODES).map(([id, n]) => {
          const on = frame.nodes.includes(id);
          return (
            <g key={id} className={`fin-nb ${on ? "on" : ""}`}>
              <rect
                x={n.x}
                y={n.y}
                width="92"
                height="30"
                rx="8"
                fill="rgba(35,35,36,0.9)"
                stroke={on ? "#e458b9" : "rgba(255,255,255,0.16)"}
                strokeWidth="1"
              />
              <text
                x={n.x + 46}
                y={n.y + 19}
                fontSize="11"
                fontWeight="600"
                textAnchor="middle"
                fill={on ? "#ffffff" : "#ababae"}
              >
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>

      <dl className="mt-5 grid grid-cols-3 gap-2 text-center sm:gap-3">
        {[
          ["Drawn", drawn],
          ["To repay", owed],
          ["Repaid", repaid],
        ].map(([name, value]) => (
          <div
            key={name}
            className="rounded-lg bg-white/5 px-2 py-2.5 ring-1 ring-white/10"
          >
            <dt className="text-[11px] uppercase tracking-wider text-brand-gray">
              {name}
            </dt>
            <dd className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-white">
              {usd.format(value)}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-brand-gray">
          <span>Repaid so far</span>
          <span className="font-mono tabular-nums">{pct.toFixed(1)}%</span>
        </div>
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-purple to-brand-pink"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <p
        className="mt-3 flex items-start gap-2 text-xs/5 text-brand-gray"
        aria-live="polite"
      >
        <span
          className={`mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full ${
            frame.done ? "bg-brand-pink" : "bg-amber-400"
          }`}
        />
        {frame.done ? (
          <span>
            <span className="font-semibold text-white">Settled.</span> The
            claim has ended.
          </span>
        ) : (
          <span>
            <span className="font-semibold text-white">Funded.</span> Any new
            deal must leave the rights holder at least {FLOOR}% of each sale.
          </span>
        )}
      </p>

      <div className="mt-3 flex justify-center">
        {frames.map((f, i) => (
          <button
            key={f.label}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Step ${i + 1} of ${frames.length}: ${f.label}`}
            aria-current={i === index ? "step" : undefined}
            className="group p-1.5"
          >
            <span
              className={`block h-2 w-2 rounded-full transition duration-300 ${
                i === index
                  ? "bg-brand-pink"
                  : "bg-[#7a7a80] group-hover:scale-125 group-hover:bg-white"
              }`}
            />
          </button>
        ))}
      </div>

      <style>{`
        @keyframes fin-fade {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes fin-flow { to { stroke-dashoffset: -18; } }
        .fin-fl {
          opacity: 0;
          transition: opacity 0.35s;
          stroke-dasharray: 6 12;
          animation: fin-flow 0.9s linear infinite;
        }
        .fin-fl.on { opacity: 1; }
        .fin-nb { opacity: 0.45; transition: opacity 0.35s; }
        .fin-nb.on { opacity: 1; }
        @media (prefers-reduced-motion: reduce) {
          .fin-fl { animation: none; }
        }
      `}</style>
    </div>
  );
}
